import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';

/** Standard views of CAD and BIM viewers; `front` looks along -z, `top` looks down. */
export type HsCameraView = 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right' | 'iso';

/** The lowest the camera's up axis may tilt towards the horizon while orbiting, as its world y. */
const MIN_UP_Y: number = 0.02;

/** Unit view direction and up axis of each standard view. */
const VIEWS: Readonly<Record<HsCameraView, { forward: readonly number[]; up: readonly number[] }>> = {
  top: { forward: [0, -1, 0], up: [0, 0, -1] },
  bottom: { forward: [0, 1, 0], up: [0, 0, 1] },
  front: { forward: [0, 0, -1], up: [0, 1, 0] },
  back: { forward: [0, 0, 1], up: [0, 1, 0] },
  left: { forward: [1, 0, 0], up: [0, 1, 0] },
  right: { forward: [-1, 0, 0], up: [0, 1, 0] },
  iso: { forward: normalize([-1, -0.8, -1]), up: [0, 1, 0] },
};

/**
 * Turns a camera world matrix (column-major) around `pivot`: `yaw` radians around the world up axis, then
 * `pitch` radians around the camera's horizontal right axis. The distance to the pivot and the pivot's place on
 * the screen stay as they are; a pitch that would tip the camera over the top or bottom is dropped.
 */
export function hsOrbitCameraMatrix(
  matrix: ArrayLike<number>,
  pivot: ArrayLike<number>,
  yaw: number,
  pitch: number
): HsHypersceneTypes.Array16 {
  const right: number[] = horizontalRight(matrix);
  const withPitch: HsHypersceneTypes.Array16 = rotateAround(matrix, pivot, right, pitch);
  const tipped: boolean = withPitch[5] < MIN_UP_Y;
  return rotateAround(tipped ? matrix : withPitch, pivot, [0, 1, 0], yaw);
}

/**
 * A camera world matrix looking at a sphere from a standard view, far enough back for the sphere to fill the
 * view. `fov` is the vertical field of view in radians, `aspect` width over height.
 */
export function hsCameraViewMatrix(
  view: HsCameraView,
  center: ArrayLike<number>,
  radius: number,
  fov: number,
  aspect: number
): HsHypersceneTypes.Array16 {
  const { forward, up } = VIEWS[view];
  return lookAlong(center, forward, up, hsFitDistance(radius, fov, aspect));
}

/**
 * Keeps the camera's view direction and moves it so a sphere fills the view, as "zoom to fit" or "zoom to
 * selection" do. A camera looking straight up or down keeps its roll.
 */
export function hsFitCameraMatrix(
  matrix: ArrayLike<number>,
  center: ArrayLike<number>,
  radius: number,
  fov: number,
  aspect: number
): HsHypersceneTypes.Array16 {
  const forward: number[] = [-matrix[8], -matrix[9], -matrix[10]];
  const up: number[] = [matrix[4], matrix[5], matrix[6]];
  return lookAlong(center, normalize(forward), normalize(up), hsFitDistance(radius, fov, aspect));
}

/** Distance from a sphere's center at which a perspective camera sees all of it, with a small margin. */
export function hsFitDistance(radius: number, fov: number, aspect: number): number {
  const vertical: number = Math.max(fov, 0.01) / 2;
  const horizontal: number = Math.atan(Math.tan(vertical) * Math.max(aspect, 0.01));
  const half: number = Math.min(vertical, horizontal);
  return (Math.max(radius, 0.01) * 1.1) / Math.sin(half);
}

function lookAlong(
  center: ArrayLike<number>,
  forward: readonly number[],
  up: readonly number[],
  distance: number
): HsHypersceneTypes.Array16 {
  const back: number[] = forward.map((value: number) => -value);
  let right: number[] = cross(up, back);
  if (length(right) < 1e-6) {
    right = cross([0, 0, -1], back);
  }
  right = normalize(right);
  const trueUp: number[] = normalize(cross(back, right));
  return [
    right[0],
    right[1],
    right[2],
    0,
    trueUp[0],
    trueUp[1],
    trueUp[2],
    0,
    back[0],
    back[1],
    back[2],
    0,
    center[0] + back[0] * distance,
    center[1] + back[1] * distance,
    center[2] + back[2] * distance,
    1,
  ];
}

function horizontalRight(matrix: ArrayLike<number>): number[] {
  const right: number[] = [matrix[0], 0, matrix[2]];
  // Looking straight up or down the right axis can lie in the plane; the back axis then points sideways.
  return length(right) > 1e-6 ? normalize(right) : normalize([matrix[10], 0, -matrix[8]]);
}

/** Rotates the camera's axes and its position around an axis through `pivot` (Rodrigues' formula). */
function rotateAround(
  matrix: ArrayLike<number>,
  pivot: ArrayLike<number>,
  axis: readonly number[],
  angle: number
): HsHypersceneTypes.Array16 {
  const result: number[] = Array.from({ length: 16 }, (_value: unknown, index: number) => matrix[index]);
  if (angle === 0) {
    return result as HsHypersceneTypes.Array16;
  }
  for (const column of [0, 4, 8]) {
    const rotated: number[] = rotate([matrix[column], matrix[column + 1], matrix[column + 2]], axis, angle);
    result[column] = rotated[0];
    result[column + 1] = rotated[1];
    result[column + 2] = rotated[2];
  }
  const offset: number[] = rotate([matrix[12] - pivot[0], matrix[13] - pivot[1], matrix[14] - pivot[2]], axis, angle);
  result[12] = pivot[0] + offset[0];
  result[13] = pivot[1] + offset[1];
  result[14] = pivot[2] + offset[2];
  return result as HsHypersceneTypes.Array16;
}

function rotate(vector: readonly number[], axis: readonly number[], angle: number): number[] {
  const cos: number = Math.cos(angle);
  const sin: number = Math.sin(angle);
  const across: number[] = cross(axis, vector);
  const along: number = dot(axis, vector) * (1 - cos);
  return [0, 1, 2].map((index: number) => vector[index] * cos + across[index] * sin + axis[index] * along);
}

function cross(a: readonly number[], b: readonly number[]): number[] {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function dot(a: readonly number[], b: readonly number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function length(vector: readonly number[]): number {
  return Math.hypot(vector[0], vector[1], vector[2]);
}

function normalize(vector: readonly number[]): number[] {
  const size: number = length(vector);
  return size === 0 ? [0, 0, 0] : vector.map((value: number) => value / size);
}
